<template>
    <div class="page">
        <Section>
            <h1 class="text-h3 font-weight-bold mb-2">{{ t('software.title') }}</h1>
            <p class="text-medium-emphasis mb-8">{{ t('software.subtitle') }}</p>
            <v-row>
                <v-col
                    v-for="p in products"
                    :key="p.key"
                    cols="12"
                    md="6"
                >
                    <v-card class="pa-6 h-100 d-flex flex-column">
                        <h2 class="text-h5 font-weight-bold">{{ p.name }}</h2>
                        <p class="text-primary mb-2">{{ p.tagline }}</p>
                        <div class="software-tags mb-3">
                            <v-chip
                                v-for="tag in p.tags"
                                :key="`${p.key}-${tag}`"
                                size="small"
                                variant="tonal"
                                color="primary"
                                class="software-tag"
                            >
                                {{ tag }}
                            </v-chip>
                        </div>
                        <p class="text-medium-emphasis flex-grow-1">{{ p.desc }}</p>
                        <div class="d-flex ga-3 mt-4 flex-wrap">
                            <v-btn
                                v-if="links[p.key].site"
                                color="primary"
                                :href="links[p.key].site"
                                target="_blank"
                                rel="noopener"
                                >{{ t('software.visit') }}</v-btn
                            >
                            <v-btn
                                variant="outlined"
                                :href="links[p.key].source"
                                target="_blank"
                                rel="noopener"
                                >{{ t('software.source') }}</v-btn
                            >
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </Section>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import Section from '@/components/Section.vue'
    import { useTranslations } from '@/composables/useTranslations'
    import { useSEO } from '@/composables/useSEO'

    const { t, get } = useTranslations()
    useSEO({
        title: () => t('seo.software.title'),
        description: () => t('seo.software.description'),
    })

    interface Product {
        key: string
        name: string
        tagline: string
        desc: string
        tags: string[]
    }
    const products = computed<Product[]>(() => get<Product[]>('software.products') ?? [])

    const links: Record<string, { site?: string; source: string }> = {
        rdatacore: {
            site: import.meta.env.VITE_RDATACORE_URL ?? 'https://rdatacore.eu',
            source:
                import.meta.env.VITE_RDATACORE_GITHUB_URL ??
                'https://github.com/BentBr/r_data_core',
        },
        myfamtree: {
            site: import.meta.env.VITE_MY_FAM_TREE_URL ?? 'https://my-fam-tree.eu',
            source:
                import.meta.env.VITE_MY_FAM_TREE_GITHUB_URL ??
                'https://github.com/BentBr/my-fam-tree',
        },
        esesaetsch: {
            source:
                import.meta.env.VITE_ESESAETSCH_GITHUB_URL ??
                'https://github.com/BentBr/esesaetsch/',
        },
    }
</script>

<style scoped>
    .page {
        padding-top: 80px;
    }

    .software-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .software-tag {
        font-weight: 700;
        letter-spacing: 0.02em;
    }
</style>
